import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Modal from '../../shared/components/ui/Modal';
import AddLeadForm from './forms/AddLeadForm';
import { UnderConsiderationCreate } from './forms/UnderConsiderationCreate';
import { ClosedWonCloseDeal } from './forms/ClosedWonCloseDeal';
import { createLead, createUnderConsideration, createClosedDeal } from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import {
  getDominoTaskFlowType,
  getDominoTaskUcDetails
} from '../utils/salesDailyTaskTypes';

const SalesDominoTaskFlow = ({ task, onClose, onComplete }) => {
  const navigate = useNavigate();
  const { projects } = useProjectsList();
  const flowType = getDominoTaskFlowType(task);
  const projectId = task?.project_id ?? task?.project?.id ?? task?.unit?.project_id;

  const [step, setStep] = useState('createLead');
  const [createdLeadId, setCreatedLeadId] = useState(null);
  const ucDetails = getDominoTaskUcDetails(task);

  const { mutate: createLeadMutation, isPending: isCreatingLead } = useMutation({
    mutationFn: (data) => createLead(data),
    onSuccess: (createdLead) => {
      const leadId = createdLead?.id ?? createdLead?.lead_id ?? createdLead?.data?.id;
      if (!leadId) {
        toast.error('Lead created but id was not returned');
        return;
      }
      toast.success('Lead created successfully');
      setCreatedLeadId(Number(leadId));
      setStep(flowType === 'dealClosed' ? 'closeDeal' : 'underConsideration');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create lead');
    }
  });

  const { mutate: createUnderConsiderationMutation, isPending: isCreatingUC } = useMutation({
    mutationFn: (data) => createUnderConsideration(data),
    onSuccess: () => {
      toast.success('Under Consideration created successfully');
      onComplete?.();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to create under consideration'
      );
    }
  });

  const { mutate: createClosedDealMutation, isPending: isClosingDeal } = useMutation({
    mutationFn: (data) => createClosedDeal(data),
    onSuccess: () => {
      toast.success('Deal closed successfully');
      onComplete?.();
      if (createdLeadId) {
        navigate(`/builder/deals/${createdLeadId}`, { state: { status: 'Closed Won' } });
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to close deal');
    }
  });

  const handleCloseFlow = () => {
    setStep('createLead');
    setCreatedLeadId(null);
    onClose?.();
  };

  if (step === 'createLead') {
    return (
      <Modal
        width={50}
        confirmOnClose
        isDirty
        name="Create Lead"
        onClose={handleCloseFlow}>
        <p className="mb-4 text-sm text-gray-600">
          Step 1 of 2: Create a lead for{' '}
          <span className="font-medium">{task?.unit?.unitName || 'this unit'}</span>.{' '}
          {flowType === 'dealClosed'
            ? 'The close deal form opens next.'
            : 'The under consideration form opens next.'}
        </p>
        <AddLeadForm
          onSubmit={createLeadMutation}
          onCancel={handleCloseFlow}
          isSubmitting={isCreatingLead}
          AllProjects={projects}
          defaultProjectId={projectId}
          lockProject={!!projectId}
        />
      </Modal>
    );
  }

  if (step === 'underConsideration') {
    return (
      <Modal
        width={50}
        confirmOnClose
        isDirty
        name="Create Under Consideration"
        onClose={handleCloseFlow}>
        <p className="mb-4 text-sm text-gray-600">
          Step 2 of 2: Add pricing and under consideration details for{' '}
          <span className="font-medium">{task?.unit?.unitName || 'the unit'}</span>.
        </p>
        <UnderConsiderationCreate
          onClose={handleCloseFlow}
          onSubmit={createUnderConsiderationMutation}
          isSubmitting={isCreatingUC}
          selectedLead={createdLeadId}
          skipFiltering
          ucDetails={ucDetails}
        />
      </Modal>
    );
  }

  if (step === 'closeDeal') {
    return (
      <Modal width={50} confirmOnClose isDirty name="Close Deal" onClose={handleCloseFlow}>
        <p className="mb-4 text-sm text-gray-600">
          Step 2 of 2: Complete deal closure for{' '}
          <span className="font-medium">{task?.unit?.unitName || 'the unit'}</span>.
        </p>
        <ClosedWonCloseDeal
          onClose={handleCloseFlow}
          onSubmit={createClosedDealMutation}
          isSubmitting={isClosingDeal}
          selectedLead={{ id: createdLeadId }}
          skipFiltering
          ucDetails={ucDetails}
        />
      </Modal>
    );
  }

  return null;
};

export default SalesDominoTaskFlow;
